import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchPharmacyProfile } from '../../../redux/middleware/user/pharmacy/profile';
import {
  CircularProgress,
  Typography,
  Box,
  Container,
  Avatar,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Business,
  Email,
  Phone,
  LocationOn,
  Schedule,
  MiscellaneousServices,
  People,
  Work
} from '@mui/icons-material';

const PharmacyDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, isAuthenticated } = useSelector((state) => state.pharmacyLogin);
  const {
    user,
    loading: profileLoading,
    profileCompletion,
    isAuthenticated: profileAuthenticated
  } = useSelector((state) => state.pharmacyProfile);

  const [fetchAttempts, setFetchAttempts] = useState(0);

  // Function to check if critical profile information is complete
  const isProfileComplete = (userData) => {
    const criticalFields = [
      userData.pharmacy_name,
      userData.owner_name,
      userData.license_number,
      userData.phone_number,
      userData.email,
      userData.address?.street,
      userData.address?.city,
      userData.address?.country,
      userData.operating_hours?.days_open?.length,
      userData.services_offered?.length
    ];

    // Check if any critical field is 'N/A' or undefined/null
    const hasIncompleteFields = criticalFields.some(field =>
      field === 'N/A' || field === undefined || field === null ||
      (Array.isArray(field) && field.length === 0)
    );

    return !hasIncompleteFields;
  };

  // Debugging
  useEffect(() => {
    console.log('Dashboard Authentication State:', {
      isAuthenticated,
      user,
      profileCompletion: user?.profile_completion
    });

    if (isAuthenticated && !user) {
      dispatch(fetchPharmacyProfile());
    }
  }, [dispatch, isAuthenticated, user]);

  useEffect(() => {
    console.log('Dashboard Authentication States:', {
      loginAuthenticated: isAuthenticated,
      profileAuthenticated,
      user,
      profileCompletion,
      fetchAttempts
    });

    // Attempt to fetch profile if not loaded
    if (isAuthenticated && (!user || profileCompletion === 0)) {
      if (fetchAttempts < 3) {
        dispatch(fetchPharmacyProfile());
        setFetchAttempts(prev => prev + 1);
      } else {
        console.error('Multiple fetch attempts failed');
        // Optionally redirect to login or show error
        navigate('/app/pharmacy/login');
      }
    }

    // Redirect logic
    if (isAuthenticated && user && profileCompletion < 100) {
      console.log(`Redirecting to profile completion. Current progress: ${profileCompletion}`);
      navigate('/app/pharmacy/profile_completion');
    }
  }, [
    dispatch,
    isAuthenticated,
    profileAuthenticated,
    user,
    profileCompletion,
    navigate,
    fetchAttempts
  ]);

  // Loading state
  if (loading || profileLoading) {
    return (
      <Container sx={{ textAlign: 'center', mt: 5 }}>
        <CircularProgress />
        <Typography>Loading your pharmacy profile...</Typography>
      </Container>
    );
  }

  // Full dashboard for completed profiles
  return (
    <Container sx={{ padding: 2, backgroundColor: '#ffffff' }}>
      <Box sx={{ maxWidth: '800px', margin: 'auto', mt: 5 }}>
        <Typography variant='h4' gutterBottom sx={{ textAlign: 'center', fontWeight: 'bold', color: '#333', fontSize: '1.5rem' }}>
          Welcome, {user.owner_name}!
        </Typography>

        {/* Pharmacy Logo */}
        {user.logo && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Avatar src={user.logo} sx={{ width: 120, height: 120 }} />
          </Box>
        )}

        {/* General Information Section */}
        <Card variant='outlined' sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant='h6'><Business sx={{ mr: 1 }} /> Pharmacy Information</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography><strong>Name:</strong> {user.pharmacy_name || 'N/A'}</Typography>
            <Typography><strong>Owner:</strong> {user.owner_name || 'N/A'}</Typography>
            <Typography><strong>License Number:</strong> {user.license_number || 'N/A'}</Typography>
          </CardContent>
        </Card>

        {/* Contact Information Section */}
        <Card variant='outlined' sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant='h6'><Phone sx={{ mr: 1 }} /> Contact Information</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography><Phone sx={{ mr: 1 }} /> {user.phone_number || 'N/A'}</Typography>
            <Typography><Email sx={{ mr: 1 }} /> {user.email || 'N/A'}</Typography>
            <Typography><LocationOn sx={{ mr: 1 }} /> {user.address?.street}, {user.address?.city}, {user.address?.country}</Typography>
          </CardContent>
        </Card>

        {/* Operating Hours Section */}
        <Card variant='outlined' sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant='h6'><Schedule sx={{ mr: 1 }} /> Operating Hours</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography><strong>Days Open:</strong> {user.operating_hours?.days_open?.join(', ') || 'N/A'}</Typography>
            <Typography><strong>Weekdays:</strong> {user.operating_hours?.weekdays?.start_time} - {user.operating_hours?.weekdays?.end_time}</Typography>
            <Typography><strong>Weekends:</strong> {user.operating_hours?.weekends?.start_time} - {user.operating_hours?.weekends?.end_time}</Typography>
          </CardContent>
        </Card>

        {/* Services Offered Section */}
        <Card variant='outlined' sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant='h6'><MiscellaneousServices sx={{ mr: 1 }} /> Services Offered</Typography>
            <Divider sx={{ mb: 2 }} />
            {user.services_offered?.length > 0
              ? (
                <List>
                  {user.services_offered.map((service, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={service.name} secondary={service.description || 'No description'} />
                    </ListItem>
                  ))}
                </List>
                )
              : <Typography>N/A</Typography>}
          </CardContent>
        </Card>

        {/* Staff Directory Section */}
        <Card variant='outlined' sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant='h6'><Work sx={{ mr: 1 }} /> Staff Directory</Typography>
            <Divider sx={{ mb: 2 }} />
            {user.staff_directory?.length > 0
              ? (
                <List>
                  {user.staff_directory.map((staff, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={staff.name} secondary={staff.position || 'No position specified'} />
                    </ListItem>
                  ))}
                </List>
                )
              : <Typography>N/A</Typography>}
          </CardContent>
        </Card>

        {/* Social Media Section */}
        <Card variant='outlined' sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant='h6'><People sx={{ mr: 1 }} /> Social Media</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography>Facebook: {user.social_media?.facebook || 'N/A'}</Typography>
            <Typography>Twitter: {user.social_media?.twitter || 'N/A'}</Typography>
            <Typography>LinkedIn: {user.social_media?.linkedin || 'N/A'}</Typography>
            <Typography>Instagram: {user.social_media?.instagram || 'N/A'}</Typography>
          </CardContent>
        </Card>

        {/* Licenses & Certifications */}
        <Card variant='outlined' sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant='h6'><Business sx={{ mr: 1 }} /> Licenses & Certifications</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography><strong>Licenses:</strong></Typography>
            {user.licenses?.length > 0
              ? (
                <List>
                  {user.licenses.map((license, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={license.name} secondary={`Issued By: ${license.issued_by} | Expiry: ${license.expiry_date}`} />
                    </ListItem>
                  ))}
                </List>
                )
              : <Typography>N/A</Typography>}

            <Typography><strong>Certifications:</strong></Typography>
            {user.certifications?.length > 0
              ? (
                <List>
                  {user.certifications.map((cert, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={cert.name} secondary={`Issued By: ${cert.issued_by} | Expiry: ${cert.expiry_date}`} />
                    </ListItem>
                  ))}
                </List>
                )
              : <Typography>N/A</Typography>}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default PharmacyDashboard;
